import React, { useEffect, useState } from 'react';
import { X, RefreshCw, Send, Paperclip, FileText, XCircle } from 'lucide-react';
import { api } from '../services/api';

const OutboundComposerModal = ({ open, onClose, contact }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachedDocuments, setAttachedDocuments] = useState([]);
  const [showDocumentPicker, setShowDocumentPicker] = useState(false);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const loadDraft = async () => {
    if (!contact?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const draft = await api.draftOutboundEmail(contact.id);
      setSubject(draft.subject || '');
      setBody(draft.body || '');
    } catch (e) {
      setError(e?.message || 'Failed to generate draft');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDocuments = async () => {
    setLoadingDocuments(true);
    try {
      const response = await api.getDocuments({ limit: 100 });
      setAvailableDocuments(response.documents || []);
    } catch (e) {
      console.error('Failed to load documents:', e);
    } finally {
      setLoadingDocuments(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadDraft();
      loadDocuments();
    } else {
      // reset state so next open feels fresh
      setError(null);
      setSubject('');
      setBody('');
      setAttachedDocuments([]);
      setShowDocumentPicker(false);
      setIsLoading(false);
      setIsSending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id]);

  const handleSend = async () => {
    if (!contact?.id) return;
    setIsSending(true);
    setError(null);
    try {
      await api.sendOutboundEmail(contact.id, { 
        subject, 
        body,
        attachment_document_ids: attachedDocuments.map(d => d.id)
      });
      onClose?.({ sent: true });
    } catch (e) {
      setError(e?.message || 'Failed to send email');
    } finally {
      setIsSending(false);
    }
  };

  const toggleDocumentAttachment = (doc) => {
    const isAttached = attachedDocuments.find(d => d.id === doc.id);
    if (isAttached) {
      setAttachedDocuments(attachedDocuments.filter(d => d.id !== doc.id));
    } else {
      setAttachedDocuments([...attachedDocuments, doc]);
    }
  };

  const removeAttachment = (docId) => {
    setAttachedDocuments(attachedDocuments.filter(d => d.id !== docId));
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('image')) return '🖼️';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
    if (mimeType?.includes('excel') || mimeType?.includes('sheet')) return '📊';
    return '📎';
  };

  if (!open) return null;

  return (
    <div style={styles.overlay} onClick={() => onClose?.({ sent: false })}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>Send Email</div>
            <div style={styles.subtitle}>
              To: <span style={{ color: '#e2e8f0' }}>{contact?.email || '(no email)'}</span>
            </div>
          </div>
          <button style={styles.iconBtn} onClick={() => onClose?.({ sent: false })}>
            <X size={18} />
          </button>
        </div>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        <div style={styles.form}>
          <label style={styles.label}>Subject</label>
          <input
            style={styles.input}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            disabled={isLoading || isSending}
          />

          <label style={styles.label}>Body</label>
          <textarea
            style={styles.textarea}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your email..."
            rows={10}
            disabled={isLoading || isSending}
          />

          {/* Attachments Section */}
          <div style={styles.attachmentsSection}>
            <div style={styles.attachmentsHeader}>
              <label style={styles.label}>Attachments</label>
              <button
                style={styles.attachButton}
                onClick={() => setShowDocumentPicker(!showDocumentPicker)}
                disabled={isLoading || isSending}
              >
                <Paperclip size={16} />
                {showDocumentPicker ? 'Hide Documents' : 'Attach Document'}
              </button>
            </div>

            {/* Attached Documents */}
            {attachedDocuments.length > 0 && (
              <div style={styles.attachedList}>
                {attachedDocuments.map(doc => (
                  <div key={doc.id} style={styles.attachedItem}>
                    <span>{getFileIcon(doc.mime_type)}</span>
                    <span style={styles.attachedName}>{doc.original_filename}</span>
                    <button
                      style={styles.removeBtn}
                      onClick={() => removeAttachment(doc.id)}
                      disabled={isSending}
                    >
                      <XCircle size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Document Picker */}
            {showDocumentPicker && (
              <div style={styles.documentPicker}>
                {loadingDocuments ? (
                  <div style={styles.loadingDocs}>Loading documents...</div>
                ) : availableDocuments.length === 0 ? (
                  <div style={styles.noDocs}>No documents available. Upload documents in Admin &gt; Knowledge &gt; Documents.</div>
                ) : (
                  <div style={styles.docList}>
                    {availableDocuments.map(doc => {
                      const isAttached = attachedDocuments.find(d => d.id === doc.id);
                      return (
                        <div
                          key={doc.id}
                          style={{
                            ...styles.docItem,
                            ...(isAttached ? styles.docItemAttached : {})
                          }}
                          onClick={() => toggleDocumentAttachment(doc)}
                        >
                          <span style={styles.docIcon}>{getFileIcon(doc.mime_type)}</span>
                          <div style={styles.docInfo}>
                            <div style={styles.docName}>{doc.original_filename}</div>
                            <div style={styles.docMeta}>{doc.category} • {(doc.size_bytes / 1024).toFixed(0)} KB</div>
                          </div>
                          {isAttached && <span style={styles.checkmark}>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={styles.footer}>
          <button
            style={{ ...styles.secondaryBtn, opacity: (isLoading || isSending) ? 0.6 : 1 }}
            onClick={loadDraft}
            disabled={isLoading || isSending}
          >
            <RefreshCw size={16} />
            Regenerate
          </button>

          <button
            style={{ ...styles.primaryBtn, opacity: (!subject.trim() || !body.trim() || isLoading || isSending) ? 0.6 : 1 }}
            onClick={handleSend}
            disabled={!subject.trim() || !body.trim() || isLoading || isSending || !contact?.email}
          >
            <Send size={16} />
            {isSending ? 'Sending…' : 'Send'}
            {attachedDocuments.length > 0 && ` (${attachedDocuments.length} attachment${attachedDocuments.length !== 1 ? 's' : ''})`}
          </button>
        </div>

        {isLoading && (
          <div style={styles.loading}>Generating draft…</div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: 16,
  },
  modal: {
    width: '100%',
    maxWidth: 720,
    maxHeight: '90vh',
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: 14,
    boxShadow: '0 20px 80px rgba(0,0,0,0.45)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: 16,
    borderBottom: '1px solid #1e293b',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  title: { color: '#f1f5f9', fontSize: 16, fontWeight: 700 },
  subtitle: { color: '#94a3b8', fontSize: 13, marginTop: 4 },
  iconBtn: {
    background: 'transparent',
    border: '1px solid #334155',
    borderRadius: 10,
    color: '#cbd5e1',
    cursor: 'pointer',
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  error: {
    padding: '10px 16px',
    background: 'rgba(239, 68, 68, 0.12)',
    borderBottom: '1px solid rgba(239, 68, 68, 0.3)',
    color: '#fecaca',
    fontSize: 13,
  },
  form: { padding: 16, display: 'flex', flexDirection: 'column', gap: 8, overflow: 'auto' },
  label: { color: '#94a3b8', fontSize: 12, fontWeight: 700, marginTop: 8 },
  input: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0b1222',
    border: '1px solid #334155',
    borderRadius: 10,
    color: '#e2e8f0',
    fontSize: 14,
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: '#0b1222',
    border: '1px solid #334155',
    borderRadius: 10,
    color: '#e2e8f0',
    fontSize: 14,
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  attachmentsSection: {
    marginTop: 8,
  },
  attachmentsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  attachButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    background: 'transparent',
    border: '1px solid var(--brand-primary, #3b82f6)',
    borderRadius: 8,
    color: 'var(--brand-primary, #3b82f6)',
    fontSize: 13,
    cursor: 'pointer',
  },
  attachedList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  attachedItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 8,
    fontSize: 13,
  },
  attachedName: {
    color: '#e2e8f0',
    maxWidth: 150,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeBtn: {
    background: 'none',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
  },
  documentPicker: {
    border: '1px solid #334155',
    borderRadius: 10,
    background: '#0b1222',
    maxHeight: 200,
    overflow: 'auto',
  },
  loadingDocs: {
    padding: 16,
    textAlign: 'center',
    color: '#94a3b8',
  },
  noDocs: {
    padding: 16,
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: 13,
  },
  docList: {
    padding: 8,
  },
  docItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 0.2s',
    ':hover': {
      background: '#1e293b',
    },
  },
  docItemAttached: {
    background: 'rgba(59, 130, 246, 0.2)',
    border: '1px solid var(--brand-primary, #3b82f6)',
  },
  docIcon: {
    fontSize: 20,
  },
  docInfo: {
    flex: 1,
    minWidth: 0,
  },
  docName: {
    color: '#e2e8f0',
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  docMeta: {
    color: '#64748b',
    fontSize: 11,
    marginTop: 2,
  },
  checkmark: {
    color: 'var(--brand-primary, #3b82f6)',
    fontWeight: 'bold',
  },
  footer: {
    padding: '12px 16px',
    borderTop: '1px solid #1e293b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  secondaryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    borderRadius: 10,
    border: '1px solid #334155',
    background: 'transparent',
    color: '#cbd5e1',
    cursor: 'pointer',
  },
  primaryBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 16px',
    borderRadius: 10,
    border: 'none',
    background: 'var(--brand-primary, #3b82f6)',
    color: '#fff',
    fontWeight: 600,
    cursor: 'pointer',
  },
  loading: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(15,23,42,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#e2e8f0',
    fontSize: 14,
  },
};

export default OutboundComposerModal;
